import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, ChevronRight, ChevronLeft, Star, Compass, BookOpen, Palette, Brain, Heart, Waves } from 'lucide-react';
import { AvatarPreview } from './AvatarBuilder';
import api from '../api';
import { toast } from 'sonner';

// Difficulty level options with kid-friendly names
const DIFFICULTY_OPTIONS = [
  {
    id: 'explorer',
    emoji: '🌱',
    name: 'Help Me Learn',
    description: 'More guidance, step-by-step help',
    color: '#4ADE80', // green
  },
  {
    id: 'path_walker',
    emoji: '⚡',
    name: 'Let Me Try',
    description: 'Balanced challenges, some hints',
    color: '#FBBF24', // amber
  },
  {
    id: 'master_builder',
    emoji: '🔥',
    name: 'Challenge Me',
    description: 'Harder questions, fewer hints',
    color: '#F472B6', // pink
  },
];

// Learning paths/portals with simple names
const LEARNING_PATHS = [
  {
    id: 'memory_garden',
    name: 'Memory Garden',
    description: 'Read stories and learn new things',
    icon: BookOpen,
    color: '#3BFF9C',
    emoji: '📚',
  },
  {
    id: 'star_map',
    name: 'Star Map Hall',
    description: 'Explore numbers and patterns',
    icon: Star,
    color: '#00E5FF',
    emoji: '⭐',
  },
  {
    id: 'creative_forge',
    name: 'Creative Forge',
    description: 'Make art and music',
    icon: Palette,
    color: '#FF9E3D',
    emoji: '🎨',
  },
  {
    id: 'radiant_will',
    name: 'Chamber of Radiant Will',
    description: 'Build strength and character',
    icon: Heart,
    color: '#FFD700',
    emoji: '💪',
  },
  {
    id: 'calm_cove',
    name: 'Calm Cove',
    description: 'Relax and feel peaceful',
    icon: Waves,
    color: '#B26BFF',
    emoji: '🌊',
  },
];

// Default avatar for quick selection
const DEFAULT_AVATARS = [
  { skin_tone: '#F5D0C5', hair_style: 'short', hair_color: '#2C1810', eye_color: '#4A3728', outfit: 'explorer_tunic', accessory: 'none' },
  { skin_tone: '#DEB887', hair_style: 'curly', hair_color: '#1C1C1C', eye_color: '#4169E1', outfit: 'star_robe', accessory: 'headband' },
  { skin_tone: '#8D6E63', hair_style: 'afro', hair_color: '#2C1810', eye_color: '#228B22', outfit: 'forest_cloak', accessory: 'glasses' },
  { skin_tone: '#A67B5B', hair_style: 'ponytail', hair_color: '#3D2314', eye_color: '#808080', outfit: 'ocean_vest', accessory: 'flower' },
  { skin_tone: '#FFE4D4', hair_style: 'braids', hair_color: '#B8860B', eye_color: '#87CEEB', outfit: 'sky_cape', accessory: 'crown' },
  { skin_tone: '#6B4423', hair_style: 'locs', hair_color: '#1C1C1C', eye_color: '#654321', outfit: 'fire_jacket', accessory: 'earrings' },
];

const FirstTimeOnboarding = ({ child, onComplete, onSkip }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0); // 0: welcome, 1: avatar, 2: path, 3: difficulty, 4: ready
  // Default to first avatar if none set
  const [selectedAvatar, setSelectedAvatar] = useState(child?.avatar || DEFAULT_AVATARS[0]);
  const [selectedPath, setSelectedPath] = useState(null);
  const [selectedDifficulty, setSelectedDifficulty] = useState('path_walker'); // default
  const [isLoading, setIsLoading] = useState(false);

  // Total steps
  const TOTAL_STEPS = 5;

  // Check if child already has an avatar
  useEffect(() => {
    if (child?.avatar && Object.keys(child.avatar).length > 0) {
      setSelectedAvatar(child.avatar);
    }
  }, [child]);

  // Handle moving to next step
  const handleNext = async () => {
    if (step === 1 && selectedAvatar) {
      // Save avatar if changed
      try {
        await api.put(`/children/${child.id}/avatar`, selectedAvatar);
        // Update localStorage
        const updatedChild = { ...child, avatar: selectedAvatar };
        localStorage.setItem('selectedChild', JSON.stringify(updatedChild));
      } catch (err) {
        console.log('Failed to save avatar:', err);
      }
    }
    
    if (step === 3 && selectedPath && selectedDifficulty) {
      // Save difficulty preference for selected path
      setIsLoading(true);
      try {
        await api.put(`/children/${child.id}/difficulty-preferences/${selectedPath}`, {
          difficulty_level: selectedDifficulty
        });
      } catch (err) {
        console.log('Failed to save difficulty preference:', err);
      }
      setIsLoading(false);
    }

    if (step < TOTAL_STEPS - 1) {
      setStep(step + 1);
    } else {
      // Final step - complete onboarding and navigate to first lesson
      handleComplete();
    }
  };

  const handleBack = () => {
    if (step > 0) {
      setStep(step - 1);
    }
  };

  const handleComplete = async () => {
    // Mark onboarding as complete
    localStorage.setItem(`onboarding_complete_${child.id}`, 'true');
    
    if (selectedPath) {
      // Navigate to the selected portal/path
      if (onComplete) {
        onComplete(selectedPath, selectedDifficulty);
      }
    } else {
      // Just close the overlay
      if (onSkip) {
        onSkip();
      }
    }
  };

  const handleSkip = () => {
    localStorage.setItem(`onboarding_complete_${child.id}`, 'true');
    if (onSkip) {
      onSkip();
    }
  };

  // Render step content
  const renderStepContent = () => {
    switch (step) {
      case 0: // Welcome
        return (
          <div className="text-center space-y-6" data-testid="onboarding-welcome">
            <div className="text-7xl animate-bounce">🏛️</div>
            <h2 className="text-3xl font-bold text-white">
              Welcome, {child?.name || 'Explorer'}!
            </h2>
            <p className="text-lg text-slate-300 max-w-md mx-auto">
              The Temple of Children is a magical place where you'll learn, play, and grow.
            </p>
            <p className="text-slate-400">
              Let's set up your adventure in just a few quick steps!
            </p>
          </div>
        );

      case 1: // Avatar Selection
        return (
          <div className="space-y-6" data-testid="onboarding-avatar">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-white mb-2">Choose Your Look</h2>
              <p className="text-slate-400">Pick an explorer that looks like you!</p>
            </div>
            
            {/* Avatar grid */}
            <div className="grid grid-cols-3 gap-4 max-w-md mx-auto">
              {DEFAULT_AVATARS.map((avatar, idx) => (
                <button
                  key={idx}
                  onClick={() => setSelectedAvatar(avatar)}
                  className={`p-3 rounded-2xl transition-all ${
                    JSON.stringify(selectedAvatar) === JSON.stringify(avatar)
                      ? 'bg-cyan-500/30 border-2 border-cyan-400 scale-105'
                      : 'bg-slate-800/50 border-2 border-transparent hover:border-slate-600'
                  }`}
                  data-testid={`avatar-option-${idx}`}
                >
                  <AvatarPreview avatar={avatar} size="md" />
                </button>
              ))}
            </div>
            
            <p className="text-center text-sm text-slate-500">
              You can customize more later in your profile!
            </p>
          </div>
        );

      case 2: // Path Selection
        return (
          <div className="space-y-6" data-testid="onboarding-path">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-white mb-2">Where Do You Want to Start?</h2>
              <p className="text-slate-400">Choose your first adventure!</p>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-lg mx-auto max-h-[50vh] overflow-y-auto pr-2">
              {LEARNING_PATHS.map((path) => {
                const Icon = path.icon;
                const isSelected = selectedPath === path.id;
                
                return (
                  <button
                    key={path.id}
                    onClick={() => setSelectedPath(path.id)}
                    className={`p-4 rounded-2xl text-left transition-all ${
                      isSelected
                        ? 'border-2 scale-[1.02]'
                        : 'border-2 border-transparent hover:border-slate-600'
                    }`}
                    style={{
                      backgroundColor: isSelected ? `${path.color}15` : 'rgba(51, 65, 85, 0.5)',
                      borderColor: isSelected ? path.color : undefined,
                    }}
                    data-testid={`path-option-${path.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl"
                        style={{ backgroundColor: `${path.color}30` }}
                      >
                        {path.emoji}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-white truncate">{path.name}</p>
                        <p className="text-sm text-slate-400 truncate">{path.description}</p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        );

      case 3: // Difficulty Selection
        return (
          <div className="space-y-6" data-testid="onboarding-difficulty">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-white mb-2">How Do You Like to Learn?</h2>
              <p className="text-slate-400">Pick what feels right for you!</p>
            </div>
            
            <div className="space-y-3 max-w-md mx-auto">
              {DIFFICULTY_OPTIONS.map((diff) => {
                const isSelected = selectedDifficulty === diff.id;
                
                return (
                  <button
                    key={diff.id}
                    onClick={() => setSelectedDifficulty(diff.id)}
                    className={`w-full p-4 rounded-2xl text-left transition-all ${
                      isSelected
                        ? 'border-2 scale-[1.02]'
                        : 'border-2 border-transparent hover:border-slate-600'
                    }`}
                    style={{
                      backgroundColor: isSelected ? `${diff.color}15` : 'rgba(51, 65, 85, 0.5)',
                      borderColor: isSelected ? diff.color : undefined,
                    }}
                    data-testid={`difficulty-option-${diff.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div 
                        className="w-14 h-14 rounded-xl flex items-center justify-center text-3xl"
                        style={{ backgroundColor: `${diff.color}30` }}
                      >
                        {diff.emoji}
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-lg text-white">{diff.name}</p>
                        <p className="text-sm text-slate-400">{diff.description}</p>
                      </div>
                      {isSelected && (
                        <div 
                          className="w-6 h-6 rounded-full flex items-center justify-center"
                          style={{ backgroundColor: diff.color }}
                        >
                          <Sparkles className="w-4 h-4 text-white" />
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
            
            <p className="text-center text-sm text-slate-500">
              You can change this anytime for any learning path!
            </p>
          </div>
        );

      case 4: // Ready to go!
        return (
          <div className="text-center space-y-6" data-testid="onboarding-ready">
            <div className="text-7xl animate-bounce">🚀</div>
            <h2 className="text-3xl font-bold text-white">
              You're Ready!
            </h2>
            <div className="flex items-center justify-center gap-2 text-lg text-slate-300">
              <span>Starting in</span>
              <span className="px-3 py-1 rounded-lg font-semibold" style={{ 
                backgroundColor: LEARNING_PATHS.find(p => p.id === selectedPath)?.color + '30',
                color: LEARNING_PATHS.find(p => p.id === selectedPath)?.color
              }}>
                {LEARNING_PATHS.find(p => p.id === selectedPath)?.name || 'the Temple'}
              </span>
            </div>
            <p className="text-slate-400 max-w-md mx-auto">
              Your first adventure awaits! Complete lessons to earn sparks and unlock cool rewards.
            </p>
          </div>
        );

      default:
        return null;
    }
  };

  // Can proceed to next step?
  const canProceed = () => {
    switch (step) {
      case 0: return true;
      case 1: return selectedAvatar !== null;
      case 2: return selectedPath !== null;
      case 3: return selectedDifficulty !== null;
      case 4: return true;
      default: return false;
    }
  };

  // Get next button text
  const getNextButtonText = () => {
    switch (step) {
      case 0: return "Let's Go!";
      case 1: return 'Choose Path';
      case 2: return 'Pick Difficulty';
      case 3: return 'Start Learning!';
      case 4: return 'Enter the Temple';
      default: return 'Next';
    }
  };

  return (
    <div 
      className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/95 backdrop-blur-md"
      data-testid="first-time-onboarding"
    >
      <div className="relative max-w-xl w-full mx-4 animate-fade-in">
        {/* Progress bar */}
        <div className="flex justify-center gap-2 mb-6">
          {Array.from({ length: TOTAL_STEPS }).map((_, idx) => (
            <div
              key={idx}
              className={`h-2 rounded-full transition-all ${
                idx === step
                  ? 'bg-cyan-400 w-8'
                  : idx < step
                  ? 'bg-cyan-600 w-2'
                  : 'bg-slate-700 w-2'
              }`}
            />
          ))}
        </div>

        {/* Main card */}
        <div className="bg-slate-900/95 border border-slate-700/50 rounded-3xl p-6 sm:p-8 shadow-2xl">
          {/* Content */}
          {renderStepContent()}

          {/* Navigation buttons */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-slate-800">
            {/* Back / Skip */}
            <div>
              {step > 0 ? (
                <button
                  onClick={handleBack}
                  className="flex items-center gap-1 px-4 py-2 text-slate-400 hover:text-white transition-colors rounded-xl hover:bg-slate-800/50"
                  data-testid="onboarding-back"
                >
                  <ChevronLeft className="w-5 h-5" />
                  Back
                </button>
              ) : (
                <button
                  onClick={handleSkip}
                  className="px-4 py-2 text-slate-500 hover:text-slate-300 transition-colors text-sm"
                  data-testid="onboarding-skip"
                >
                  Skip for now
                </button>
              )}
            </div>

            {/* Next */}
            <button
              onClick={handleNext}
              disabled={!canProceed() || isLoading}
              className={`flex items-center gap-2 px-6 py-3 rounded-full font-semibold transition-all ${
                canProceed() && !isLoading
                  ? 'bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 text-white shadow-lg shadow-cyan-500/30 hover:scale-105'
                  : 'bg-slate-800 text-slate-500 cursor-not-allowed'
              }`}
              data-testid="onboarding-next"
            >
              {isLoading ? (
                'Saving...'
              ) : (
                <>
                  {getNextButtonText()}
                  <ChevronRight className="w-5 h-5" />
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FirstTimeOnboarding;
